import React, { useContext, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import DatePicker from 'react-date-picker';
import { TextField } from '@mui/material';
import moment from 'moment';
import * as styles from './modal.styles';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import Announcement from '../models/announcement';
import Row from '../../../core/components/row/row.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import AnnouncementFormType from '../enums/announcement-form-types';
import StyleConstants from '../../../core/styles.constants';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly handleCreate: (announcement: Announcement) => void;
  readonly isAnnouncementValid: (announcement: Announcement) => boolean;
  readonly officeId: string;
}

const CreateAnnouncementModal = ({ showModal, handleClose, handleCreate, isAnnouncementValid, officeId }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const innitialAnnouncement = new Announcement();
  innitialAnnouncement.officeId = officeId;
  const [announcement, setAnnouncement] = useState<Announcement>(innitialAnnouncement);
  const { t } = useTranslation();

  const resetModalState = (): void => {
    const newAnnouncement = new Announcement();
    newAnnouncement.officeId = officeId;
    setAnnouncement(newAnnouncement);
  };

  const handleCloseModal = (): void => {
    resetModalState();
    handleClose();
  };

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!showModal) {
      setIsLoading(false);
    }
  }, [showModal]);

  const handleSave = (): void => {
    const announcementCopy = { ...announcement };
    if (isAnnouncementValid(announcementCopy)) {
      setIsLoading(true);
      resetModalState();
      handleCreate(announcementCopy);
      setIsLoading(false);
    }
  };

  const handleAnnouncementChange = (formType: AnnouncementFormType, value: unknown): void => {
    const announcementCopy = { ...announcement };
    switch (formType) {
      case AnnouncementFormType.message:
        announcementCopy.message = value as string;
        break;
      case AnnouncementFormType.startDate:
        announcementCopy.startDate = moment
          .utc(value as Date)
          .add((value as Date).getTimezoneOffset() * -1, 'm')
          .toDate() as Date;
        break;
      case AnnouncementFormType.endDate:
        if (value == null) {
          announcementCopy.endDate = value as Date | undefined;
        } else {
          announcementCopy.endDate = moment(value).endOf('day').toDate();
        }
        break;
      case AnnouncementFormType.isNonWorkingDay:
        announcementCopy.isNonWorkingDay = value as boolean;
        break;
      default:
        break;
    }
    setAnnouncement(announcementCopy);
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCloseModal} style={styles.modalStyle(isMobile, 800, 'visible')}>
      <Column className={style(styles.modalBody(isMobile))} itemSpacing={28} mainAxisAlignment={MainAxisAlignment.Stretch}>
        <Row>
          <span className={style(styles.columnHeader(isMobile, 24))}>{t('create-announcement')}</span>
        </Row>

        <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
          <TextField
            label={t('message').toString()}
            multiline
            maxRows={2}
            value={announcement.message}
            onChange={(event): void => {
              handleAnnouncementChange(AnnouncementFormType.message, event.target.value.toString());
            }}
          />
        </Row>

        <Row crossAxisAlignment={CrossAxisAlignment.Center} mainAxisAlignment={MainAxisAlignment.SpaceBetween}>
          <span className={style(styles.modalText)}>{t('choose-the-start-day')}</span>
          <DatePicker
            clearIcon={null}
            allowPartialRange
            calendarClassName={style(styles.calendar)}
            format="dd/MM/yyyy"
            value={announcement.startDate}
            onChange={(value): void => {
              handleAnnouncementChange(AnnouncementFormType.startDate, value);
            }}
          />

          <span className={style(styles.modalText)}>{t('choose-the-end-day')}</span>
          <DatePicker
            clearIcon={null}
            calendarClassName={style(styles.calendar)}
            format="dd/MM/yyyy"
            value={announcement.endDate}
            onChange={(value): void => {
              handleAnnouncementChange(AnnouncementFormType.endDate, value);
            }}
          />
        </Row>

        <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={10}>
          <Switch
            checked={announcement.isNonWorkingDay}
            onChange={(value): void => {
              handleAnnouncementChange(AnnouncementFormType.isNonWorkingDay, value);
            }}
            onColor={StyleConstants.ORANGE_COLOR}
            onHandleColor={StyleConstants.WHITE_COLOR}
            handleDiameter={styles.switchDiameter}
            uncheckedIcon={false}
            checkedIcon={false}
            boxShadow={styles.switchBoxShadow}
            activeBoxShadow={styles.switchActiveBoxShadow}
            height={styles.switchHeight}
            width={styles.switchWidth}
          />
          <span className={style(styles.modalText)}>{t('is-non-working-day')}</span>
        </Row>

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              onClick={(): void => {
                handleSave();
              }}
              loading={isLoading}
            >
              {t('create')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleCloseModal}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default CreateAnnouncementModal;
